import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

/**
 * Bronson Select component.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-select.html
 *
 * @param {SelectItem[]} [children] - Collection of {@link SelectItem} components.
 * @param {string} [className] - Additional modifiers.
 * @param {boolean} [disabled] - Mark the Select as disabled.
 * @param {boolean} [error] - Mark the Select as invalid.
 * @param {string} [id] - Identifier for the `<select>` element.
 * @param {string} [name] - Value for the `[name]` attribute for the `<select>` element.
 * @param {boolean} [success] - Mark the Select as valid.
 * @param {string} [testId] - The `[data-testid]` attribute on the component element.
 * @param {object} [otherProps] - Other props passed to the `<select>` element. Replaces 'select-attribute'.
 * @returns {JSX.Element} - The {@link Select} component.
 * @constructor
 */
export function Select({ error, disabled, name, id, testId, children, className, success, ...otherProps }) {
  const divClassNameList = classNames(
    {
      'is-disabled': disabled,
    },
    'c-input c-input--select',
    className
  ).trim()

  const selectClassNameList = classNames('c-input__input', {
    'is-error': error,
    'is-success': success,
  }).trim()

  return (
    <div className={divClassNameList}>
      <select
        {...otherProps}
        disabled={disabled}
        data-testid={testId}
        className={selectClassNameList}
        name={name}
        id={id}
      >
        {children /* Use 'Select.Item' component. */}
      </select>
    </div>
  )
}

Select.propTypes = {
  error: PropTypes.bool, // Bronson template: 'select-is-error'.
  success: PropTypes.bool, // Bronson template: 'select-is-success'.
  name: PropTypes.string, // Bronson template: 'select-name'.
  id: PropTypes.string, // Bronson template: 'select-id'.
  disabled: PropTypes.bool, // Disabled prop of {@link Select}.
  className: PropTypes.string, // Custom CSS classes.
  testId: PropTypes.string, // Added for `[data-testid]` attribute.
  children: PropTypes.node, // Bronson template: 'select-item'. Use {@link SelectItem} component.
}

/**
 * Bronson SelectItem component (nested).
 *
 * @param {string} [children] - The SelectItem label.
 * @param {string} [value] - The SelectItem value.
 * @param {object} [otherProps] - Other props passed to the `<option>` element.
 * @returns {JSX.Element} - The SelectItem component.
 * @constructor
 */
function SelectItem({ children, value, ...otherProps }) {
  return (
    <option {...otherProps} value={value}>
      {children}
    </option>
  )
}

SelectItem.propTypes = {
  children: PropTypes.string, // Bronson template: 'select-item-label'.
  value: PropTypes.string, // Bronson template: 'select-item-value'.
}

SelectItem.displayName = 'Select.Item'
Select.Item = SelectItem
